/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669680000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Nov 29 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "azure-functions",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to your Azure functions",
          "depth": 1,
          "id": "connect-to-your-azure-functions"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Choose your function triggers",
          "depth": 1,
          "id": "choose-your-function-triggers"
        }, {
          "value": "Configure initial sync behavior",
          "depth": 1,
          "id": "configure-initial-sync-behavior"
        }, {
          "value": "Configure batch size",
          "depth": 1,
          "id": "configure-batch-size"
        }, {
          "value": "Configure rate and concurrency limits",
          "depth": 1,
          "id": "configure-rate-and-concurrency-limits"
        }, {
          "value": "Configure error handling",
          "depth": 1,
          "id": "configure-error-handling"
        }, {
          "value": "Invocation payload schema",
          "depth": 1,
          "id": "invocation-payload-schema"
        }, {
          "value": "Response payload schema",
          "depth": 1,
          "id": "response-payload-schema"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      img: "img",
      code: "code",
      strong: "strong",
      em: "em",
      pre: "pre",
      span: "span",
      h4: "h4",
      h5: "h5",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["This destination invokes a ", _jsx(_components.a, {
          href: "https://azure.microsoft.com/en-us/products/functions/",
          children: "Microsoft Azure"
        }), " function whenever data changes in your model. It makes it possible to build your own custom integrations that execute code written in any language that Azure functions ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/azure-functions/supported-languages",
          children: "supports"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "This destination was designed to be as flexible as possible. You can exercise granular control over function triggers, batching, rate limits, concurrency limits, and even error handling. Together, these features let you integrate Hightouch with any internal tool or third-party API."
      }), "\n", _jsx(_components.p, {
        children: "Under the hood, the Azure functions destination gives you Hightouch's powerful sync engine, so you continue to benefit from the security and observability features available in our native SaaS destinations."
      }), "\n", _jsx(_components.p, {
        children: "Example use cases include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Syncing data to web APIs not yet natively supported"
        }), "\n", _jsx(_components.li, {
          children: "Enriching data using external sources like Clearbit and ZoomInfo"
        }), "\n", _jsx(_components.li, {
          children: "Transforming and filtering data using code instead of SQL"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-azure-functions",
        children: "Connect to your Azure functions"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up the Azure functions destination for the first time, you need to enter your function's ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/azure-functions/functions-bindings-http-webhook-trigger?tabs=in-process%2Cfunctionsv2&pivots=programming-language-javascript#customize-the-http-endpoint",
          children: "URL"
        }), " and it's ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/azure-functions/functions-bindings-http-webhook-trigger?tabs=in-process%2Cfunctionsv2&pivots=programming-language-javascript#authorization-keys",
          children: "access key"
        }), " for authorizing invocations."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.p, {
        children: "Once you've connected to your Azure function in your Hightouch workspace, the next step is to configure a sync that invokes your function whenever rows are added, changed, or removed in your model."
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-function-triggers",
        children: "Choose your function triggers"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch monitors your data model for added, changed, and/or removed rows. In this step, you specify which of these events should invoke your function."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-serverless-triggers.png",
          alt: "Declaring which events should invoke the Azure function in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Suppose you want to use an Azure function to send a confirmation email whenever a customer places a new order or updates an existing order. Your model might look something like ", _jsx(_components.code, {
          children: "SELECT * FROM orders"
        }), ". The Azure function should be triggered whenever rows are added—for example, when customers place new orders—or whenever rows change—when orders are updated. Therefore, you would want to enable the ", _jsx(_components.strong, {
          children: "Rows added"
        }), " and ", _jsx(_components.strong, {
          children: "Rows changed"
        }), " triggers. When invoking your function, Hightouch passes along metadata about ", _jsx(_components.em, {
          children: "why"
        }), " the function was invoked."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "All function invocations are synchronous, meaning that Hightouch invokes your Azure function and waits for its response."
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-initial-sync-behavior",
        children: "Configure initial sync behavior"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you tell Hightouch how to handle rows present in your model results during the first sync run."
      }), "\n", _jsx(_components.p, {
        children: "Certain workflows, such as hydrating a CRM for the first time, may require performing a backfill of all rows during the initial sync. For other use cases, such as sending confirmation emails, you might only want to invoke your function in response to future data changes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-serverless-backfill.png",
          alt: "Declaring how to handle initial sync behavior in Hightouch"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-batch-size",
        children: "Configure batch size"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch separately invokes your Azure function for each added, changed, or removed row."
      }), "\n", _jsx(_components.p, {
        children: "Certain high-throughput use cases may require batching together multiple rows in the payload. Hightouch supports batches of up to 1000 rows per function invocation."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-serverless-batching.png",
          alt: "Declaring batch size in Hightouch"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Batching isn't recommended unless absolutely necessary for performance reasons. Enabling this feature requires your Azure function to be either idempotent or significantly more fault tolerant in the event of a partial batch failure."
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-rate-and-concurrency-limits",
        children: "Configure rate and concurrency limits"
      }), "\n", _jsx(_components.p, {
        children: "In this step, you declare whether function invocations should be throttled."
      }), "\n", _jsx(_components.p, {
        children: "To determine the ideal limits, you should consider whether your function interacts with any downstream services that have limits of their own."
      }), "\n", _jsx(_components.p, {
        children: "Most modern web APIs enforce rate limits, which set a maximum allowed number of requests per second, minute, or hour. Occasionally, APIs may also have concurrency limits, which set a maximum allowed number of requests that can be processed simultaneously. Rate limits and concurrency limits both affect overall sync speed."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-azure-functions-rate-limit.png",
          alt: "Configuring rate and concurrency limits in Hightouch"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-error-handling",
        children: "Configure error handling"
      }), "\n", _jsx(_components.p, {
        children: "Azure functions can fail for many reasons. Hightouch's retry logic for this destination doesn't discriminate between invocation errors, for example, missing permissions, runtime errors, for example, due to syntax issues, or function errors, for example, due to uncaught exceptions. Hightouch retries all errors eventually."
      }), "\n", _jsx(_components.p, {
        children: "In this step, you decide whether errors should be retried immediately or during the next sync run. If you choose to retry immediately, you can specify how many retries should be attempted during the sync run. If all these retries fail, Hightouch retires the request during subsequent sync runs until it succeeds."
      }), "\n", _jsxs(_components.p, {
        children: ["If an error is gracefully caught in your function, you can flag it as a rejected row and provide a custom error message using the response format specified in the ", _jsx(_components.a, {
          href: "#configure-error-handling",
          children: "error handling section"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-serverless-retry.png",
          alt: "Error handling declaration"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You can also include retry logic inside of your Azure function. After exhausting all retry attempts during a function invocation, respond with an error so that Hightouch knows to reinvoke the function later."
        })
      }), "\n", _jsx(_components.h3, {
        id: "invocation-payload-schema",
        children: "Invocation payload schema"
      }), "\n", _jsx(_components.p, {
        children: "The payload, also known as the \"event document,\" contains your row data in JSON format, along with additional metadata. When writing your Azure function, you can assume that all function invocations include a payload following this schema:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"operation\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"primary_key_column\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"rows\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            XXXXXX\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"metadata\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"api_version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sync_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sync_run_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "invocation-payload-properties",
        children: "Invocation payload properties"
      }), "\n", _jsx(_components.h5, {
        id: "operation",
        children: _jsx(_components.code, {
          children: "operation"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The row operation, also known as the trigger, explains ", _jsx(_components.em, {
          children: "why"
        }), " your function was invoked."]
      }), "\n", _jsxs(_components.p, {
        children: ["Its possible values are these strings: ", _jsx(_components.code, {
          children: "\"add\""
        }), ", ", _jsx(_components.code, {
          children: "\"change\""
        }), ", and ", _jsx(_components.code, {
          children: "\"remove\""
        }), "."]
      }), "\n", _jsx(_components.h5, {
        id: "primary_key_column",
        children: _jsx(_components.code, {
          children: "primary_key_column"
        })
      }), "\n", _jsx(_components.p, {
        children: "This string refers to the column name of the primary key for your Hightouch model."
      }), "\n", _jsx(_components.h5, {
        id: "rows",
        children: _jsx(_components.code, {
          children: "rows"
        })
      }), "\n", _jsx(_components.p, {
        children: "This is an array of objects, with each object containing row data from your Hightouch model."
      }), "\n", _jsx(_components.h5, {
        id: "metadata",
        children: _jsx(_components.code, {
          children: "metadata"
        })
      }), "\n", _jsx(_components.p, {
        children: "This is an object containing three key/value pairs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "api_version"
          }), " (integer): currently set to ", _jsx(_components.code, {
            children: "1"
          }), ", this identifier only changes if the payload schema is modified in the future"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync_id"
          }), " (integer): this is the unique identifier for the Hightouch sync configuration associated with the function invocation"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync_run_id"
          }), " (integer): this is the unique identifier for the specific sync run associated with the function invocation"]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "example-invocation-payload",
        children: "Example invocation payload"
      }), "\n", _jsx(_components.p, {
        children: "Suppose a new row is added to your model:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "customer_id"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "email_address"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "first_name"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "last_name"
              })
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "928713"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "john.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "John"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "The payload for the function invocation would look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"operation\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"add\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"primary_key_column\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"rows\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"928713\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"john.doe@example.com\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"John\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"last_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"metadata\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"api_version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sync_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sync_run_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Note that ", _jsx(_components.code, {
            children: "rows"
          }), " is an ", _jsx(_components.strong, {
            children: "array"
          }), " containing a single element."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "example-invocation-payload-for-batching",
        children: "Example invocation payload for batching"
      }), "\n", _jsx(_components.p, {
        children: "Suppose three new rows are added to your model:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "customer_id"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "email_address"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "first_name"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "last_name"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "928713"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "alice.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Alice"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "283743"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "bob.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Bob"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "162352"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "carol.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Carol"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "The payload for the function invocation would look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"operation\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"add\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"primary_key_column\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"rows\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"928713\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alice.doe@example.com\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Alice\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"last_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"283743\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"bob.doe@example.com\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Bob\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"last_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customer_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"162352\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"carol.doe@example.com\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"first_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Carol\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"last_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"metadata\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"api_version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sync_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sync_run_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["When batching is enabled, rows sharing a common operation type—", _jsx(_components.code, {
            children: "add"
          }), ", ", _jsx(_components.code, {
            children: "change"
          }), ", ", _jsx(_components.code, {
            children: "remove"
          }), "—are batched together. Function invocations always represent exactly one operation type."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "response-payload-schema",
        children: "Response payload schema"
      }), "\n", _jsx(_components.p, {
        children: "If your Azure function fails to process any rows, it can respond with an array of \"rejected rows\" that encountered errors. Each rejected row should be identified by its primary key/value and may be associated with an optional error message. Hightouch retries these rows."
      }), "\n", _jsx(_components.p, {
        children: "If there is an error, the response payload looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"errors\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"primary_key_value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"reason\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " XXXXX\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "response-payload-properties",
        children: "Response payload properties"
      }), "\n", _jsxs(_components.p, {
        children: ["Errors in the ", _jsx(_components.code, {
          children: "errors"
        }), " array have these properties:"]
      }), "\n", _jsx(_components.h5, {
        id: "primary_key_value",
        children: _jsx(_components.code, {
          children: "primary_key_value"
        })
      }), "\n", _jsx(_components.p, {
        children: "This identifies the rejected row that needs to be retried."
      }), "\n", _jsxs(_components.h5, {
        id: "reason-optional",
        children: [_jsx(_components.code, {
          children: "reason"
        }), " (optional)"]
      }), "\n", _jsx(_components.p, {
        children: "This string may represent an error message or any other information that would be helpful for debugging and monitoring sync health."
      }), "\n", _jsx(_components.h4, {
        id: "example-success-response-payload",
        children: "Example success response payload"
      }), "\n", _jsx(_components.p, {
        children: "If all rows are processed successfully, your function responds with an empty payload."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "example-response-payload-for-rejected-rows",
        children: "Example response payload for rejected rows"
      }), "\n", _jsx(_components.p, {
        children: "Suppose three new rows are added to your model:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "customer_id"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "email_address"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "first_name"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.code, {
                children: "last_name"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "928713"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "alice.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Alice"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "283743"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "bob.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Bob"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "162352"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "carol.doe@example.com"
              })
            }), _jsx(_components.td, {
              children: "Carol"
            }), _jsx(_components.td, {
              children: "Doe"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "While processing these rows, your Azure function encounters two errors:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Alice's email can't be found in the downstream service"
        }), "\n", _jsx(_components.li, {
          children: "Bob's email already exists in the downstream service"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To surface these errors in Hightouch, your function should respond like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"errors\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"primary_key_value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"928713\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"reason\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Email not found\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"primary_key_value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"283743\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"reason\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Email already exists\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The primary key values (", _jsx(_components.code, {
            children: "928713"
          }), " and ", _jsx(_components.code, {
            children: "283743"
          }), ") refer to ", _jsx(_components.code, {
            children: "customer_id"
          }), " values. The ", _jsx(_components.code, {
            children: "customer_id"
          }), " column was designated the ", _jsx(_components.code, {
            children: "primary_key_column"
          }), " in the function invocation payload."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "example-response-payload-for-other-errors",
        children: "Example response payload for other errors"
      }), "\n", _jsx(_components.p, {
        children: "If your function experiences an invocation error, for example, exceeding Azure function rate limits, runtime error, for example, due to syntax issues, or function error, for example due to uncaught exceptions, all rows in the batch are automatically marked as rejected rows."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
